import {
  Box,
  VStack,
  Flex,
  Separator,
  useBreakpointValue,
  Button,
  Icon,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLogoutMutation } from "@/store/authApi";
import SidebarBrand from "./SidebarBrand";
import NavItemComponent from "./NavItemComponent";
import NavSection from "./NavSection";
import LogoutButton from "./LogoutButton";
import { navItems, groupItemsBySection } from "./navItems";
import { sidebarStyles, collapsedSidebarStyles, toggleButtonStyles } from "./styles";
import { useSidebarLayout } from "./SidebarLayoutContext";
import type { SidebarProps } from "./types";

export default function Sidebar({ isOpen = true, isCollapsed = false, onToggle }: SidebarProps) {
  const { setSidebar } = useSidebarLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  // Responsive display
  const display = useBreakpointValue({ base: isOpen ? "block" : "none", lg: "block" });

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
      navigate("/login");
    }
  };

  // Dynamically group items by section
  const { mainItems, sections, sectionOrder } = groupItemsBySection(navItems);

  return (
    <Box ref={setSidebar} {...(isCollapsed ? collapsedSidebarStyles : sidebarStyles)} display={display}>
      <VStack gap={6} align="stretch" p={isCollapsed ? 2 : 4} h="full">
        {/* Brand and Toggle */}
        {isCollapsed ? (
          // Only toggle button when collapsed
          onToggle && (
            <Button
              {...toggleButtonStyles}
              onClick={onToggle}
              title="Expand sidebar"
              w="auto"
              minW="auto"
            >
              <Icon as={ChevronRight} boxSize={4} />
            </Button>
          )
        ) : (
          // Brand and toggle when expanded
          <Flex align="center" justify="space-between">
            <SidebarBrand isCollapsed={isCollapsed} />
            {onToggle && (
              <Button
                {...toggleButtonStyles}
                onClick={onToggle}
                title="Collapse sidebar"
                w="auto"
                minW="auto"
              >
                <Icon as={ChevronLeft} boxSize={4} />
              </Button>
            )}
          </Flex>
        )}
        
        <Separator />

        <Flex direction="column" gap={6} flex={1} overflow="auto">
          {/* Main navigation */}
          <VStack gap={1} align="stretch">
            {mainItems.map((item) => (
              <NavItemComponent
                key={item.path}
                item={item}
                isActive={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                isCollapsed={isCollapsed}
              />
            ))}
          </VStack>

          {/* Dynamic sections */}
          {sectionOrder.map((sectionName) => (
            <Box key={sectionName}>
              {!isCollapsed && <Separator />}
              <NavSection
                title={sectionName}
                items={sections[sectionName]}
                currentPath={location.pathname}
                onNavigate={navigate}
                isCollapsed={isCollapsed}
              />
            </Box>
          ))}
        </Flex>

        {/* Logout section */}
        <Box>
          <Separator mb={4} />
          <LogoutButton onLogout={handleLogout} isLoading={isLoggingOut} isCollapsed={isCollapsed} />
        </Box>
      </VStack>
    </Box>
  );
} 